import { getFrontendConfigLoader } from '@doxyme/config';
import { type LocationDetails } from './users.types';

/**
 * Shape returned by the public `users/userLocationDetails` REST endpoint on
 * api-core. The endpoint may also return a JSON `null` body when CloudFront
 * headers are unavailable (e.g. local development) in
 * which case this module resolves to `null`.
 */
type ApiCoreUserLocationDetails = {
  clientIp: string;
  location: {
    countryCode: string;
    countryName: string | null;
    regionName: string | null;
    city: string | null;
    latitude: number | null;
    longitude: number | null;
    currency: { code: string } | null;
    connection: {
      asn: number | null;
      isp: string | null;
    };
  };
};

function mapToLocationDetails(
  raw: ApiCoreUserLocationDetails,
): LocationDetails {
  return {
    clientIp: raw.clientIp ?? '',
    location: {
      country_code: raw.location.countryCode ?? '',
      country_name: raw.location.countryName ?? '',
      region_name: raw.location.regionName ?? '',
      city: raw.location.city ?? undefined,
      latitude: raw.location.latitude ?? undefined,
      longitude: raw.location.longitude ?? undefined,
      currency: raw.location.currency?.code ?? undefined,
      connection: raw.location.connection
        ? {
            asn: raw.location.connection.asn ?? undefined,
            isp: raw.location.connection.isp ?? undefined,
          }
        : undefined,
    },
  };
}

/**
 * Tracking-only fetch from api-core's public REST endpoint. Used by
 * `useTrackUserLocation` for Segment
 */
export async function fetchUserLocationDetails(): Promise<LocationDetails | null> {
  const apiCoreUrl = getFrontendConfigLoader().get('API_CORE_URL') as
    | string
    | undefined;
  if (!apiCoreUrl) {
    return null;
  }

  const response = await fetch(`${apiCoreUrl}/users/userLocationDetails`, {
    method: 'GET',
    credentials: 'omit',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch user location details (HTTP ${response.status})`,
    );
  }

  const raw = (await response.json()) as ApiCoreUserLocationDetails | null;
  if (!raw?.location?.countryCode) {
    return null;
  }
  return mapToLocationDetails(raw);
}
